import { useState, useEffect, useMemo, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import { createPortal } from "react-dom";
import Potrace from "potrace";
import { finishApplyLoading } from "./stitchApplyLoading";
import {
  buildPotraceBitmapCanvas,
  collectLayerPathDs,
  isCanvasEffectivelyEmpty,
  PIXEL_ERASE_POTRACE_PARAMS,
} from "./stitchPotraceBitmap";
import { computeSignedDistanceField, morphImageData } from "../../lib/utils/morphology";

const MORPH_SCALE = 2; // canvas bitmap pixels per CSS pixel
const ACCENT = "#0ea5e9";
const MORPH_MAX_MM = 3; // slider range: ±3 mm

/**
 * Map Potrace bitmap pixel coords back to SVG viewBox coordinates.
 */
function scaleAndOffsetPath(d, scale, offsetX, offsetY) {
  if (!d) return d;
  let coordIdx = 0;
  return d.replace(
    /[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g,
    (n) => {
      const isX = coordIdx % 2 === 0;
      coordIdx++;
      const scaled = parseFloat(n) * scale;
      return (scaled + (isX ? offsetX : offsetY)).toFixed(2);
    },
  );
}

/**
 * Expand / Shrink (dilate / erode) overlay for the Stitch Editor.
 *
 * Rasterizes the selected layer, computes a signed distance field once, then a
 * slider drives a live grow/shrink preview (a cheap threshold pass per tick).
 * On Apply the previewed shape is re-vectorized via Potrace and replaces the
 * layer path — the same commit flow as the pixel erase tool.
 */
const StitchMorphCanvas = forwardRef(({
  svgJoined,
  svgSplit,
  layerIdx,
  colors,
  layoutMetrics,
  pan,
  zoom,
  onApply,   // called with (layerIdx, newPathD) — keeps tool open
}, ref) => {
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const baseImageDataRef = useRef(null); // pristine rasterized layer
  const distFieldRef = useRef(null);     // SDF for baseImageDataRef
  const applyExitResolverRef = useRef(null);
  const hasWorkRef = useRef(false);

  const [amountMm, setAmountMm] = useState(0); // >0 expand, <0 shrink
  const [isProcessing, setIsProcessing] = useState(false);
  const [applyError, setApplyError] = useState(null);
  // Set right before we hand a freshly-applied path back to the parent. The
  // parent's svgJoined/svgSplit update flows back down as new props, which
  // would otherwise re-trigger the rasterize-from-scratch effect below and
  // wipe/re-decode the canvas asynchronously — racing the already-correct
  // pixels we just cached and occasionally leaving the preview blank or
  // stale for a frame. Skip that one reset since our cached base is already
  // in sync with the just-committed shape.
  const justAppliedRef = useRef(false);

  // Resolve the toolbar's controls slot after mount (it commits in the same
  // pass as this overlay, so it isn't in the DOM during our first render).
  const [controlsSlot, setControlsSlot] = useState(null);
  useEffect(() => {
    let raf;
    const find = () => {
      const el = document.getElementById("stitch-erase-controls-slot");
      if (el) { setControlsSlot(el); return; }
      raf = requestAnimationFrame(find);
    };
    find();
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, []);

  const { dw, dh, vbX, vbY, vbW, vbH, hoopScale } = layoutMetrics;
  const W = Math.round(dw * MORPH_SCALE);
  const H = Math.round(dh * MORPH_SCALE);
  // hoopScale = CSS px per mm, so bitmap px per mm is hoopScale * MORPH_SCALE
  const pxPerMm = (hoopScale || 4) * MORPH_SCALE;

  const layerColor = colors?.[layerIdx] || "#000000";

  // Faint ghost of every other layer so the user can judge the grow/shrink
  // offset relative to neighboring shapes (e.g. a fill under an outline).
  const ghostLayers = useMemo(() => {
    const source = svgSplit || svgJoined;
    if (!source?.children?.length) return null;
    const layers = source.children.map((layer, i) => {
      if (i === layerIdx) return null;
      const dStrings = collectLayerPathDs(layer);
      if (!dStrings.length) return null;
      const fillRule = layer?.attributes?.["fill-rule"] || layer?.attributes?.fillRule || "evenodd";
      return { key: i, d: dStrings.join(" "), color: colors?.[i] || "#888888", fillRule };
    }).filter(Boolean);
    return layers.length ? layers : null;
  }, [svgJoined, svgSplit, layerIdx, colors]);

  // ── Rasterize current layer onto the canvas ───────────────────────────────

  const captureBase = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !canvas.width) return;
    try {
      const ctx = canvas.getContext("2d");
      baseImageDataRef.current = ctx.getImageData(0, 0, W, H);
      distFieldRef.current = null; // recompute lazily for the new base
    } catch (err) {
      console.warn("[StitchMorphCanvas] captureBase failed:", err);
      baseImageDataRef.current = null;
    }
  }, [W, H]);

  const rasterizeLayer = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const source = svgSplit || svgJoined;
    if (!source?.children?.[layerIdx]) return;

    const layer = source.children[layerIdx];
    const viewBox = source.attributes?.viewBox || "0 0 1000 1000";

    const dStrings = collectLayerPathDs(layer);
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, W, H);

    if (dStrings.length === 0) {
      captureBase();
      return;
    }

    const fillRule = layer?.attributes?.["fill-rule"] || layer?.attributes?.fillRule || "evenodd";
    const compoundD = dStrings.join(" ");
    const svgStr =
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" width="${W}" height="${H}">` +
      `<path d="${compoundD}" fill="${layerColor}" fill-rule="${fillRule}" />` +
      `</svg>`;

    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0, W, H);
      captureBase();
    };
    img.onerror = () => {
      const [vbx, vby, vbw, vbh] = viewBox.split(/\s+/).map(Number);
      ctx.save();
      ctx.translate(-vbx * (W / vbw), -vby * (H / vbh));
      ctx.scale(W / vbw, H / vbh);
      ctx.fillStyle = layerColor;
      try {
        const combinedPath = new Path2D(compoundD);
        ctx.fill(combinedPath, fillRule);
      } catch (_) { /* skip on unsupported path */ }
      ctx.restore();
      captureBase();
    };

    try {
      const encoded = btoa(unescape(encodeURIComponent(svgStr)));
      img.src = "data:image/svg+xml;base64," + encoded;
    } catch (_) { img.onerror(); }
  }, [svgJoined, svgSplit, layerIdx, layerColor, W, H, captureBase]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (justAppliedRef.current) {
      justAppliedRef.current = false;
      return;
    }
    canvas.width = W;
    canvas.height = H;
    setAmountMm(0);
    setApplyError(null);
    rasterizeLayer();
  }, [W, H, rasterizeLayer]);

  // ── Live preview: threshold the SDF at the slider amount ─────────────────

  useEffect(() => {
    const canvas = canvasRef.current;
    const base = baseImageDataRef.current;
    if (!canvas || !base) return;

    const ctx = canvas.getContext("2d");

    if (!amountMm) {
      ctx.clearRect(0, 0, W, H);
      ctx.putImageData(base, 0, 0);
      hasWorkRef.current = false;
      return;
    }

    if (!distFieldRef.current) {
      distFieldRef.current = computeSignedDistanceField(base);
    }

    const outImg = morphImageData(base, distFieldRef.current, amountMm * pxPerMm, layerColor, ctx);
    ctx.clearRect(0, 0, W, H);
    ctx.putImageData(outImg, 0, 0);
    hasWorkRef.current = true;
  }, [amountMm, pxPerMm, layerColor, W, H]);

  // ── Apply (Potrace) ───────────────────────────────────────────────────────

  const applyStartRef = useRef(0);
  const applyEndTimerRef = useRef(null);

  const handleApply = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      applyExitResolverRef.current?.(false);
      applyExitResolverRef.current = null;
      return;
    }
    applyStartRef.current = Date.now();
    setIsProcessing(true);
    setApplyError(null);

    let bwCanvas;
    let capturedImageData;
    try {
      // The canvas already shows the morphed shape — vectorize it as-is.
      const imageData = canvas.getContext("2d").getImageData(0, 0, W, H);
      capturedImageData = imageData;

      if (isCanvasEffectivelyEmpty(imageData)) {
        finishApplyLoading(applyStartRef.current, setIsProcessing, applyEndTimerRef, () => {
          hasWorkRef.current = false;
          // Adopt a blank base immediately so the neutral-slider redraw below
          // doesn't flash back to the pre-apply shape while the parent's path
          // update round-trips back down as props.
          baseImageDataRef.current = canvas.getContext("2d").createImageData(W, H);
          distFieldRef.current = null;
          justAppliedRef.current = true;
          setAmountMm(0);
          onApply?.(layerIdx, "");
          applyExitResolverRef.current?.(true);
          applyExitResolverRef.current = null;
        });
        return;
      }

      bwCanvas = buildPotraceBitmapCanvas(imageData, W, H);
    } catch (err) {
      console.error("[StitchMorphCanvas] Canvas read error:", err);
      setIsProcessing(false);
      setApplyError("Could not read canvas. Try again.");
      applyExitResolverRef.current?.(false);
      applyExitResolverRef.current = null;
      return;
    }

    bwCanvas.toBlob((blob) => {
      if (!blob) {
        setIsProcessing(false);
        setApplyError("Failed to encode. Try again.");
        applyExitResolverRef.current?.(false);
        applyExitResolverRef.current = null;
        return;
      }
      const blobUrl = URL.createObjectURL(blob);
      const timeoutId = setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
        setIsProcessing(false);
        setApplyError("Vectorization timed out. Try again.");
        applyExitResolverRef.current?.(false);
        applyExitResolverRef.current = null;
      }, 15000);

      const trace = new Potrace.Potrace();
      trace.setParameters(PIXEL_ERASE_POTRACE_PARAMS);

      trace.loadImage(blobUrl, (loadErr) => {
        clearTimeout(timeoutId);
        URL.revokeObjectURL(blobUrl);
        if (loadErr) {
          setIsProcessing(false);
          setApplyError("Vectorization failed. Try again.");
          applyExitResolverRef.current?.(false);
          applyExitResolverRef.current = null;
          return;
        }
        try {
          const pathTag = trace.getPathTag();
          const match = pathTag?.match(/d="([^"]+)"/);
          if (!match) {
            finishApplyLoading(applyStartRef.current, setIsProcessing, applyEndTimerRef, () => {
              hasWorkRef.current = false;
              baseImageDataRef.current = canvas.getContext("2d").createImageData(W, H);
              distFieldRef.current = null;
              justAppliedRef.current = true;
              setAmountMm(0);
              onApply?.(layerIdx, "");
              applyExitResolverRef.current?.(true);
              applyExitResolverRef.current = null;
            });
            return;
          }
          const newPathD = scaleAndOffsetPath(match[1], vbW / W, vbX, vbY);
          finishApplyLoading(applyStartRef.current, setIsProcessing, applyEndTimerRef, () => {
            hasWorkRef.current = false;
            // Adopt the already-rendered morphed pixels as the new pristine
            // base so the neutral-slider redraw shows the just-applied shape
            // right away instead of flashing back to the pre-apply shape
            // while the parent's path update round-trips back down as props.
            baseImageDataRef.current = capturedImageData;
            distFieldRef.current = null;
            justAppliedRef.current = true;
            setAmountMm(0);
            onApply?.(layerIdx, newPathD);
            applyExitResolverRef.current?.(true);
            applyExitResolverRef.current = null;
          });
        } catch (err) {
          console.error("[StitchMorphCanvas] Potrace error:", err);
          setIsProcessing(false);
          setApplyError("Vectorization failed. Try again.");
          applyExitResolverRef.current?.(false);
          applyExitResolverRef.current = null;
        }
      });
    }, "image/png");
  }, [W, H, vbX, vbY, vbW, layerIdx, onApply]);

  useEffect(() => () => {
    if (applyEndTimerRef.current) clearTimeout(applyEndTimerRef.current);
  }, []);

  // Snap the slider back to neutral — the layer is untouched until Apply.
  const handleDiscard = useCallback(() => {
    if (isProcessing) return;
    setAmountMm(0);
    setApplyError(null);
  }, [isProcessing]);

  useImperativeHandle(ref, () => ({
    hasWorkToApply: () => hasWorkRef.current,
    applyAndExit: () => new Promise((resolve) => {
      if (isProcessing || !hasWorkRef.current) {
        resolve(false);
        return;
      }
      applyExitResolverRef.current = resolve;
      handleApply();
    }),
  }), [isProcessing, handleApply]);

  // ── Render ────────────────────────────────────────────────────────────────

  const hasWork = amountMm !== 0;
  const sliderPct = ((amountMm + MORPH_MAX_MM) / (2 * MORPH_MAX_MM)) * 100;
  const valueLabel = `${amountMm > 0 ? "+" : ""}${amountMm.toFixed(1)} mm`;

  return (
    <>
      <style>{`@keyframes stitch-ai-spin { to { transform: rotate(360deg); } }`}</style>
      {/* Morph preview overlay — no pointer interaction, panning passes through */}
      <div
        ref={overlayRef}
        data-pan-zoom="design"
        data-dl={layoutMetrics.designLeft}
        data-dt={layoutMetrics.designTop}
        style={{
          position: "absolute",
          top: 0, left: 0,
          width: `${dw}px`,
          height: `${dh}px`,
          transformOrigin: "0 0",
          transform: `translate(${pan.x + layoutMetrics.designLeft * zoom}px, ${pan.y + layoutMetrics.designTop * zoom}px) scale(${zoom})`,
          zIndex: 50,
          pointerEvents: "none",
          userSelect: "none",
        }}
      >
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height: "100%", display: "block" }}
        />

        {/* Other layers, ghosted above the preview so their boundaries stay
            visible while the morphed shape grows or shrinks against them. */}
        {ghostLayers && (
          <svg
            viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
            preserveAspectRatio="none"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              opacity: 0.3,
              pointerEvents: "none",
            }}
          >
            {ghostLayers.map((g) => (
              <path key={g.key} d={g.d} fill={g.color} fillRule={g.fillRule} />
            ))}
          </svg>
        )}

        {isProcessing && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              background: "transparent",
              pointerEvents: "none",
              zIndex: 2,
            }}
          >
            <div
              style={{
                width: "28px",
                height: "28px",
                borderRadius: "50%",
                border: `3px solid rgba(14,165,233,0.2)`,
                borderTop: `3px solid ${ACCENT}`,
                animation: "stitch-ai-spin 0.7s linear infinite",
                filter: "drop-shadow(0 1px 2px rgba(255,255,255,0.85))",
              }}
            />
            <span style={{
              fontSize: "11px",
              fontWeight: 600,
              color: ACCENT,
              fontFamily: "Clash Grotesk, sans-serif",
              textShadow: "0 1px 3px rgba(255,255,255,0.95), 0 0 6px rgba(255,255,255,0.75)",
            }}>
              Applying…
            </span>
          </div>
        )}
      </div>

      {/* Controls portaled into the shared tool-controls slot */}
      {controlsSlot && createPortal(
        <div style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: "10px" }}>
          {/* Amount slider — center is neutral, left shrinks, right expands */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "11px", color: "#888", fontFamily: "Clash Grotesk, sans-serif", whiteSpace: "nowrap" }}>
              Shrink
            </span>
            <input
              type="range"
              data-tutorial="offset-amount"
              aria-label="Offset amount in millimetres"
              min={-MORPH_MAX_MM}
              max={MORPH_MAX_MM}
              step={0.1}
              value={amountMm}
              disabled={isProcessing}
              onChange={(e) => setAmountMm(Number(e.target.value))}
              onDoubleClick={() => setAmountMm(0)}
              style={{
                flex: 1,
                accentColor: ACCENT,
                cursor: isProcessing ? "not-allowed" : "pointer",
                opacity: isProcessing ? 0.5 : 1,
                background: `linear-gradient(to right, #e5e7eb 0%, #e5e7eb ${Math.min(50, sliderPct)}%, ${ACCENT} ${Math.min(50, sliderPct)}%, ${ACCENT} ${Math.max(50, sliderPct)}%, #e5e7eb ${Math.max(50, sliderPct)}%, #e5e7eb 100%)`,
              }}
            />
            <span style={{ fontSize: "11px", color: "#888", fontFamily: "Clash Grotesk, sans-serif", whiteSpace: "nowrap" }}>
              Expand
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{
              fontSize: "12px",
              fontWeight: 700,
              color: hasWork ? ACCENT : "#9ca3af",
              fontFamily: "Clash Grotesk, sans-serif",
              fontVariantNumeric: "tabular-nums",
            }}>
              {valueLabel}
            </span>
          </div>

          {!isProcessing && !applyError && (
            <span style={{
              fontSize: "11px",
              fontWeight: 500,
              color: ACCENT,
              fontFamily: "Clash Grotesk, sans-serif",
              lineHeight: 1.35,
            }}>
              {hasWork
                ? "Adjust the slider, then Apply — or Discard"
                : "Drag the slider to grow or shrink this layer's shape"}
            </span>
          )}

          {isProcessing && (
            <span style={{
              fontSize: "11px",
              color: ACCENT,
              fontFamily: "Clash Grotesk, sans-serif",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}>
              <span style={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                border: "2px solid rgba(14,165,233,0.25)",
                borderTop: `2px solid ${ACCENT}`,
                animation: "stitch-ai-spin 0.7s linear infinite",
                display: "inline-block",
                flexShrink: 0,
              }} />
              Applying…
            </span>
          )}
          {applyError && (
            <span style={{ fontSize: "11px", color: "#ef4444", fontFamily: "Clash Grotesk, sans-serif" }}>
              {applyError}
            </span>
          )}

          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={handleDiscard}
              disabled={isProcessing || !hasWork}
              style={{
                flex: 1,
                border: "1px solid rgba(0,0,0,0.1)",
                background: "#fff",
                color: "#888",
                fontSize: "11px",
                fontWeight: 600,
                padding: "7px 12px",
                borderRadius: "8px",
                cursor: (isProcessing || !hasWork) ? "not-allowed" : "pointer",
                fontFamily: "Clash Grotesk, sans-serif",
                opacity: (isProcessing || !hasWork) ? 0.5 : 1,
              }}
            >
              Discard
            </button>
            <button
              data-tutorial="offset-apply"
              onClick={handleApply}
              disabled={isProcessing || !hasWork}
              style={{
                flex: 1,
                border: "none",
                background: (isProcessing || !hasWork) ? "#f3f4f6" : `linear-gradient(135deg, ${ACCENT}, #0284c7)`,
                color: (isProcessing || !hasWork) ? "#9ca3af" : "#fff",
                fontSize: "11px",
                fontWeight: 700,
                padding: "7px 12px",
                borderRadius: "8px",
                cursor: (isProcessing || !hasWork) ? "not-allowed" : "pointer",
                fontFamily: "Clash Grotesk, sans-serif",
              }}
            >
              Apply
            </button>
          </div>
        </div>,
        controlsSlot,
      )}
    </>
  );
});

export default StitchMorphCanvas;
